import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    CircularProgress,
    TextField,
    Typography,
    Box,
    IconButton,
    makeStyles
} from '@material-ui/core';
import { TimesIcon } from '../../icons';
import { useNotify } from '../../hooks/useNotify';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import { cancelReasonAutocomplete } from '../../autocomplete';
import AutocompleteSelect from '../atom/AutocompleteSelect';
import { useReduxState } from '../../hooks/useReduxState';
import postActiviterMessage from '../../utils/postActiviterMessage';

const useStyles = makeStyles(theme => ({
    paper: { borderRadius: 14 },
    header: {
        padding: '16px 20px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    title: { fontSize: 17, fontWeight: 700, color: theme.palette.text.primary },
    body: { padding: '16px 20px' },
    section: { marginBottom: 16 },
    sectionDivided: {
        marginBottom: 16,
        paddingTop: 16,
        borderTop: `1px solid ${theme.palette.divider}`
    },
    fieldLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.primary,
        marginBottom: 8
    },
    policyText: {
        fontSize: 14,
        color: theme.palette.text.secondary,
        lineHeight: 1.5
    },
    seg: {
        display: 'flex',
        width: '100%',
        border: `1px solid ${theme.palette.divider}`,
        padding: 3,
        borderRadius: 8,
        background: theme.palette.background.paper
    },
    segBtn: {
        flex: 1,
        border: 0,
        background: 'transparent',
        padding: '6px 12px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        fontFamily: 'inherit',
        '&:hover': { color: theme.palette.text.primary }
    },
    segBtnActive: {
        background: theme.palette.text.primary,
        color: theme.palette.background.paper,
        '&:hover': { color: theme.palette.background.paper }
    },
    segBtnDangerActive: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': { color: theme.palette.error.contrastText }
    },
    reasonDesc: {
        marginTop: 8,
        padding: '8px 12px',
        background: theme.palette.action.hover,
        borderLeft: `3px solid ${theme.palette.divider}`,
        borderRadius: 2
    }
}));

const REFUND_POLICY = 'policy';
const REFUND_FORCE_FULL = 'full';
const REFUND_FORCE_NONE = 'none';

function Seg({ options, value, onChange, classes }) {
    return (
        <div className={classes.seg}>
            {options.map(opt => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        className={[
                            classes.segBtn,
                            active && opt.danger ? classes.segBtnDangerActive : '',
                            active && !opt.danger ? classes.segBtnActive : ''
                        ].join(' ')}
                        onClick={() => onChange(opt.value)}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function AgentCancelForm({ classes, agentOptions, setAgentOptions }) {
    const refundMode = agentOptions.forceFullRefund
        ? REFUND_FORCE_FULL
        : agentOptions.forceNoRefund
        ? REFUND_FORCE_NONE
        : REFUND_POLICY;

    const setRefundMode = mode =>
        setAgentOptions({
            ...agentOptions,
            forceFullRefund: mode === REFUND_FORCE_FULL,
            forceNoRefund: mode === REFUND_FORCE_NONE
        });

    return (
        <>
            <div className={classes.sectionDivided}>
                <div className={classes.fieldLabel}>Refund behavior</div>
                <Seg
                    classes={classes}
                    value={refundMode}
                    onChange={setRefundMode}
                    options={[
                        { value: REFUND_POLICY, label: 'Default' },
                        { value: REFUND_FORCE_FULL, label: 'Force full refund' },
                        { value: REFUND_FORCE_NONE, label: 'Force no refund', danger: true }
                    ]}
                />
            </div>

            <div className={classes.sectionDivided}>
                <div className={classes.fieldLabel} style={{ marginBottom: 14 }}>
                    Cancel reason
                </div>
                <AutocompleteSelect
                    initCall={cancelReasonAutocomplete.init}
                    autocompleteCall={cancelReasonAutocomplete.search}
                    value={agentOptions.cancelReason}
                    setValue={(newValue, getById) => {
                        const item = newValue != null ? getById(newValue) : null;
                        setAgentOptions({
                            ...agentOptions,
                            cancelReason: newValue,
                            cancelReasonDescription: item?.description || ''
                        });
                    }}
                >
                    <TextField label="Cancel reason" variant="outlined" size="small" />
                </AutocompleteSelect>
                {agentOptions.cancelReasonDescription && (
                    <Box className={classes.reasonDesc}>
                        <Typography variant="caption" color="textSecondary">
                            {agentOptions.cancelReasonDescription}
                        </Typography>
                    </Box>
                )}
                <TextField
                    style={{ marginTop: 12 }}
                    placeholder="Internal note (optional)"
                    value={agentOptions.note}
                    onChange={ev =>
                        setAgentOptions({
                            ...agentOptions,
                            note: ev.target.value
                        })
                    }
                    multiline
                    fullWidth
                    variant="outlined"
                    size="small"
                    minRows={3}
                />
            </div>
        </>
    );
}

function CancellationModal({ bookingItem, onCancelFinished, showModal, MODAL_TYPE }) {
    const classes = useStyles();
    const reduxState = useReduxState();
    const role = reduxState.user?.user?.role || 'guest';
    const isAgent = ['agent', 'admin'].includes(role);

    const [loading, setLoading] = useState(false);
    const [bookingItemInternal, setBookingItem] = useState(bookingItem);
    const [agentOptions, setAgentOptions] = useState({
        note: '',
        forceFullRefund: false,
        forceNoRefund: false,
        cancelReason: null,
        cancelReasonDescription: ''
    });
    const { notifySuccess, notifyError } = useNotify();

    useEffect(() => {
        if (bookingItem) {
            setBookingItem(bookingItem);
        }
    }, [bookingItem]);

    const close = useCallback(() => showModal(MODAL_TYPE.NONE), [showModal, MODAL_TYPE]);

    const cancelOrder = useCallback(async () => {
        setLoading(true);
        try {
            const data = await doSwaggerCall('bookings', 'cancelBookingItem', {
                bookingItemId: bookingItemInternal.id,
                cancelBookingItemData: agentOptions
            });
            setBookingItem({
                ...bookingItemInternal,
                refund: data.refund,
                status: data.refund ? 'cancelled_full_refund' : 'cancelled'
            });
            onCancelFinished(bookingItemInternal?.id, data.refund);
            notifySuccess('Booking cancelled successfully');
            showModal(MODAL_TYPE.NONE);
            postActiviterMessage({
                type: 'BOOKING_CANCELLED',
                bookingItemId: bookingItemInternal?.id,
                refund: !!data.refund
            });
        } catch (err) {
            notifyError(err.toString());
        } finally {
            setLoading(false);
        }
    }, [
        agentOptions,
        bookingItemInternal,
        onCancelFinished,
        notifyError,
        notifySuccess,
        showModal,
        MODAL_TYPE
    ]);

    if (!bookingItem) {
        return null;
    }

    const bookingActive =
        bookingItemInternal?.status !== 'cancelled' &&
        bookingItemInternal?.status !== 'cancelled_full_refund';

    const title = bookingActive ? 'Cancel booking' : 'Booking cancelled';

    const policyMessage = bookingActive
        ? `This cannot be undone. Please review ${bookingItemInternal?.activity?.name}'s cancellation policy to ensure that your cancellation is eligible for a refund.`
        : bookingItemInternal?.refund
        ? `Please allow up to ${bookingItemInternal?.activity?.cancellation_window} hours for your card to be refunded.`
        : `Your request falls outside of ${bookingItemInternal?.activity?.name}'s cancellation window or less than ${bookingItemInternal?.activity?.cancellation_window} hours prior to the scheduled activity and therefore will not be refunded. Thank you for understanding.`;

    return (
        <Dialog
            aria-labelledby="cancellation-modal"
            maxWidth="sm"
            fullWidth
            open
            onClose={close}
            classes={{ paper: classes.paper }}
        >
            <div className={classes.header}>
                <span className={classes.title} id="cancellation-modal">
                    {title}
                </span>
                <IconButton size="small" onClick={close} disabled={loading}>
                    <TimesIcon />
                </IconButton>
            </div>

            <DialogContent dividers style={{ padding: 0 }}>
                <div className={classes.body}>
                    <div className={classes.section}>
                        <div className={classes.policyText}>{policyMessage}</div>
                    </div>
                    {bookingActive && isAgent && (
                        <AgentCancelForm
                            classes={classes}
                            agentOptions={agentOptions}
                            setAgentOptions={setAgentOptions}
                        />
                    )}
                </div>
            </DialogContent>

            {loading ? (
                <DialogActions>
                    <CircularProgress size={24} />
                </DialogActions>
            ) : (
                <DialogActions>
                    {bookingActive ? (
                        <>
                            <Button onClick={close}>Keep booking</Button>
                            <Button variant="contained" color="secondary" onClick={cancelOrder}>
                                Yes, cancel booking
                            </Button>
                        </>
                    ) : (
                        <Button variant="contained" color="primary" onClick={close}>
                            Close
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
}

export default CancellationModal;
